import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/common/ScrollToTop";

const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Projects = lazy(() => import("./pages/Projects"));
const DynamicProjectPage = lazy(() => import("./pages/DynamicProjectPage"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const KVKKDisclosure = lazy(() => import("./pages/KVKKDisclosure"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Insights = lazy(() => import("./pages/Insights"));
const InsightArticle = lazy(() => import("./pages/InsightArticle"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const HomeEditor = lazy(() => import("./pages/admin/HomeEditor"));
const ProjectsManager = lazy(() => import("./pages/admin/ProjectsManager"));
const ProjectEditor = lazy(() => import("./pages/admin/ProjectEditor"));
const AboutEditor = lazy(() => import("./pages/admin/AboutEditor"));
const ContactEditor = lazy(() => import("./pages/admin/ContactEditor"));
const ContactLeads = lazy(() => import("./pages/admin/ContactLeads"));
const InsightsManager = lazy(() => import("./pages/admin/InsightsManager"));
const InsightsEditor = lazy(() => import("./pages/admin/InsightsEditor"));
const EmailDashboard = lazy(() => import("./pages/admin/EmailDashboard"));
const SEODashboard = lazy(() => import("./pages/admin/SEODashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Index />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/projects/:slug" element={<DynamicProjectPage />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/kvkk" element={<KVKKDisclosure />} />
                    <Route path="/cookie-policy" element={<CookiePolicy />} />
                    <Route path="/insights" element={<Insights />} />
                    <Route path="/insights/:slug" element={<InsightArticle />} />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />

                    {/* Admin routes */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/admin/home" element={<HomeEditor />} />
                    <Route path="/admin/projects" element={<ProjectsManager />} />
                    <Route path="/admin/projects/new" element={<ProjectEditor />} />
                    <Route path="/admin/projects/edit/:id" element={<ProjectEditor />} />
                    <Route path="/admin/about" element={<AboutEditor />} />
                    <Route path="/admin/contact" element={<ContactEditor />} />
                    <Route path="/admin/contact/leads" element={<ContactLeads />} />
                    <Route path="/admin/insights" element={<InsightsManager />} />
                    <Route path="/admin/insights/new" element={<InsightsEditor />} />
                    <Route path="/admin/insights/edit/:id" element={<InsightsEditor />} />
                    <Route path="/admin/emails" element={<EmailDashboard />} />
                    <Route path="/admin/seo" element={<SEODashboard />} />

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
